// AdminMarketPublish.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Divider,
  Grid,
  LinearProgress,
  MenuItem,
  Typography,
  TextField,
  ListItemText,
} from "@mui/material";

import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

import {
  collection,
  deleteDoc,
  doc,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
} from "firebase/firestore";
import { deleteObject, getDownloadURL, ref, uploadBytesResumable } from "firebase/storage";

import UnifiedMarketCard from "./UnifiedMarketCard"; // проверь путь
import { db, storage, auth } from "./firebase"; // проверь путь

/* ===== categories ===== */
const TOP_RIGHT = ["Politics", "Sports", "Finance", "Crypto", "Game", "Tech", "World", "Economy"];

/* ===== filter tree ===== */
const TREE = {
  Sports: {
    chips: ["Live", "Futures"],
    sub: [
      "American Football",
      "Basketball",
      "Hockey",
      "Tennis",
      "Esports",
      "Baseball",
      "Football",
      "Cricket",
      "Golf",
      "UFC",
      "Formula 1",
      "Chess",
      "Boxing",
    ],
  },
  Finance: {
    chips: ["All", "Daily", "Weekly", "Monthly"],
    sub: ["Stocks", "Earnings", "Indices", "Commodities", "Forex", "Acquisitions", "Earnings Calls", "IPOs", "Fed Rates"],
  },
  Crypto: {
    chips: ["All", "Pump", "Dump", "Flat", "Pre-Market", "ETF"],
    sub: ["Bitcoin", "Ethereum", "Solana", "XRP", "Dogecoin", "ALTCOIN", "Microstrategy", "MEME"],
  },
  Politics: {
    chips: [],
    sub: ["USA", "China", "Russia", "Venezuela", "Iran", "South Korea", "Ukraine", "Gaza", "Israel"],
  },
  Game: {
  chips: ["All", "PC", "Console", "Mobile", "Esports"],
  sub: ["Dota 2", "CS2", "LoL", "Valorant", "Fortnite", "GTA", "Minecraft"],
},

};

const FLAGS = ["New", "Hot", "Breaking"];

/* ===== safe defaults ===== */
const DEFAULT_UI = {
  glass: "linear-gradient(135deg, rgba(18,22,28,.86) 0%, rgba(10,13,17,.86) 100%)",
  border: "1px solid rgba(255,255,255,.10)",
  borderSoft: "1px solid rgba(255,255,255,.08)",
  text: "#e5e7eb",
  subtext: "#9ca3af",
  accent: "#38bdf8",
};

function makeDefaultFieldSx(UI) {
  return {
    "& .MuiInputLabel-root": { color: "rgba(229,231,235,.86)" },
    "& .MuiInputLabel-root.Mui-focused": { color: UI.accent },
    "& .MuiOutlinedInput-root": {
      color: UI.text,
      background: "rgba(2,6,23,.38)",
      borderRadius: 2,
      "& fieldset": { borderColor: "rgba(255,255,255,.18)" },
      "&:hover fieldset": { borderColor: "rgba(56,189,248,.40)" },
      "&.Mui-focused fieldset": { borderColor: "rgba(56,189,248,.60)" },
    },
    "& .MuiFormHelperText-root": { color: "rgba(148,163,184,.9)" },
  };
}

function safeExt(name = "", fallback = "bin") {
  const m = String(name).toLowerCase().match(/\.([a-z0-9]+)$/);
  return m?.[1] || fallback;
}

function slugifyTitle(s = "") {
  const base = String(s || "event")
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-zA-Z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .toLowerCase();
  return base || "event";
}

/**
 * Загрузка файла в Storage
 * stream => avatar_streams/{docId}/{kind}.{ext}
 * prediction => market_items/{docId}/{kind}.{ext}
 */
async function uploadFile({ storage, docId, file, kind, type, onProgress }) {
  if (!file) return "";

  const isVideo = kind === "video" || kind === "preview";
  const isLow = kind === "posterLow" || kind === "imageLow";

  const ext = isVideo ? safeExt(file.name, "mp4") : isLow ? "jpg" : safeExt(file.name, "jpg");

  const basePath = type === "stream" ? `avatar_streams/${docId}` : `market_items/${docId}`;
  const path = `${basePath}/${kind}.${ext}`;

  const storageRef = ref(storage, path);

  const task = uploadBytesResumable(storageRef, file, {
    contentType: file.type || undefined,
    cacheControl: "public,max-age=31536000,immutable",
  });

  const url = await new Promise((resolve, reject) => {
    task.on(
      "state_changed",
      (snap) => {
        const p = snap.totalBytes ? (snap.bytesTransferred / snap.totalBytes) * 100 : 0;
        onProgress?.(Math.round(p));
      },
      (err) => reject(err),
      async () => resolve(await getDownloadURL(task.snapshot.ref))
    );
  });

  return url;
}

/** безопасное удаление файла по URL (если нет — игнорируем ошибку) */
async function safeDeleteFile(storage, url) {
  if (!url) return;
  try {
    const r = ref(storage, url); // ref поддерживает full download URL (v9)
    await deleteObject(r);
  } catch (e) {
    console.warn("[AdminMarketPublish] delete file failed:", url, e?.message || e);
  }
}

/** prediction: posterLow из картинки */
async function makeImageLow(file, maxW = 480, quality = 0.72) {
  if (!file) return null;

  const url = URL.createObjectURL(file);
  try {
    const img = new Image();
    img.decoding = "async";
    img.src = url;
    await new Promise((res, rej) => {
      img.onload = res;
      img.onerror = rej;
    });

    const scale = Math.min(1, maxW / img.naturalWidth);
    const w = Math.max(1, Math.round(img.naturalWidth * scale));
    const h = Math.max(1, Math.round(img.naturalHeight * scale));

    const c = document.createElement("canvas");
    c.width = w;
    c.height = h;

    const ctx = c.getContext("2d");
    ctx.drawImage(img, 0, 0, w, h);

    const blob = await new Promise((res) => c.toBlob(res, "image/jpeg", quality));
    if (!blob) return null;

    return new File([blob], "posterLow.jpg", { type: "image/jpeg" });
  } finally {
    URL.revokeObjectURL(url);
  }
}

/** stream: poster из видео (первый кадр) */
async function capturePosterFromVideoFile(file, seekTo = 0.2, maxW = 960, quality = 0.82) {
  if (!file) return null;

  const url = URL.createObjectURL(file);
  try {
    const video = document.createElement("video");
    video.src = url;
    video.muted = true;
    video.playsInline = true;
    video.preload = "metadata";

    await new Promise((resolve, reject) => {
      const onLoaded = () => resolve();
      const onErr = () => reject(new Error("Failed to load video metadata for poster"));
      video.addEventListener("loadedmetadata", onLoaded, { once: true });
      video.addEventListener("error", onErr, { once: true });
    });

    const t = Math.min(Math.max(seekTo, 0), Math.max((video.duration || 1) - 0.05, 0));
    video.currentTime = t;

    await new Promise((resolve) => {
      const onSeeked = () => resolve();
      video.addEventListener("seeked", onSeeked, { once: true });
      setTimeout(resolve, 450);
    });

    const vw = video.videoWidth || 1280;
    const vh = video.videoHeight || 720;

    const scale = Math.min(1, maxW / vw);
    const w = Math.max(1, Math.round(vw * scale));
    const h = Math.max(1, Math.round(vh * scale));

    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;

    const ctx = canvas.getContext("2d");
    ctx.drawImage(video, 0, 0, w, h);

    const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", quality));
    if (!blob) return null;

    return new File([blob], "poster.jpg", { type: "image/jpeg" });
  } finally {
    URL.revokeObjectURL(url);
  }
}

export default function AdminMarketPublish({ UI: UIProp, fieldSx: fieldSxProp, onCreated } = {}) {
  const UI = UIProp || DEFAULT_UI;
  const fieldSx = fieldSxProp || makeDefaultFieldSx(UI);

  const MENU_PROPS = useMemo(
    () => ({
      PaperProps: {
        sx: {
          background: "rgba(6,8,12,.98)",
          color: UI.text,
          border: "1px solid rgba(255,255,255,.12)",
          backdropFilter: "blur(10px)",
        },
      },
    }),
    [UI]
  );

  const [err, setErr] = useState(null);
  const [saving, setSaving] = useState(false);

  // 1) TYPE
  const [type, setType] = useState("stream"); // stream | prediction

  // 2) CHIPS
  const [streamStatus, setStreamStatus] = useState("live"); // live | record (only for stream)
  const [category, setCategory] = useState("Crypto");
  const [flags, setFlags] = useState(["New"]);

  // 3) MarketSub / MarketChip
  const [marketSub, setMarketSub] = useState("");
  const [marketChip, setMarketChip] = useState("");

  // 4) Title / SubTitle
  const [title, setTitle] = useState("");
  const [subTitle, setSubTitle] = useState("");

  // 5) Files — НОВЫЙ КОНТРАКТ
  // stream: preview + video
  const [previewFile, setPreviewFile] = useState(null);
  const [videoFile, setVideoFile] = useState(null);

  // prediction: poster (+ auto posterLow)
  const [posterFile, setPosterFile] = useState(null);
  const [posterLowFile, setPosterLowFile] = useState(null);

  // optional
  const [avatarFile, setAvatarFile] = useState(null);

  // stream auto preview poster (для UI-предпросмотра)
  const [autoStreamPosterFile, setAutoStreamPosterFile] = useState(null);
  const [autoStreamPosterLowFile, setAutoStreamPosterLowFile] = useState(null);

  const [progress, setProgress] = useState({
    video: 0,
    preview: 0,
    poster: 0,
    posterLow: 0,
    avatar: 0,
  });

  // ===== Preview URLs (stable; no blob ERR_FILE_NOT_FOUND) =====
const [previewPreviewUrl, setPreviewPreviewUrl] = useState("");
const [videoPreviewUrl, setVideoPreviewUrl] = useState("");
const [posterPreviewUrl, setPosterPreviewUrl] = useState("");
const [posterLowPreviewUrl, setPosterLowPreviewUrl] = useState("");
const [avatarPreviewUrl, setAvatarPreviewUrl] = useState("");
const [autoStreamPosterPreviewUrl, setAutoStreamPosterPreviewUrl] = useState("");
const [autoStreamPosterLowPreviewUrl, setAutoStreamPosterLowPreviewUrl] = useState("");

// previewFile -> previewPreviewUrl
useEffect(() => {
  if (!previewFile) {
    setPreviewPreviewUrl("");
    return;
  }
  const u = URL.createObjectURL(previewFile);
  setPreviewPreviewUrl(u);
  return () => URL.revokeObjectURL(u);
}, [previewFile]);

// videoFile -> videoPreviewUrl
useEffect(() => {
  if (!videoFile) {
    setVideoPreviewUrl("");
    return;
  }
  const u = URL.createObjectURL(videoFile);
  setVideoPreviewUrl(u);
  return () => URL.revokeObjectURL(u);
}, [videoFile]);

// posterFile -> posterPreviewUrl
useEffect(() => {
  if (!posterFile) {
    setPosterPreviewUrl("");
    return;
  }
  const u = URL.createObjectURL(posterFile);
  setPosterPreviewUrl(u);
  return () => URL.revokeObjectURL(u);
}, [posterFile]);

// posterLowFile -> posterLowPreviewUrl
useEffect(() => {
  if (!posterLowFile) {
    setPosterLowPreviewUrl("");
    return;
  }
  const u = URL.createObjectURL(posterLowFile);
  setPosterLowPreviewUrl(u);
  return () => URL.revokeObjectURL(u);
}, [posterLowFile]);

// avatarFile -> avatarPreviewUrl
useEffect(() => {
  if (!avatarFile) {
    setAvatarPreviewUrl("");
    return;
  }
  const u = URL.createObjectURL(avatarFile);
  setAvatarPreviewUrl(u);
  return () => URL.revokeObjectURL(u);
}, [avatarFile]);

// autoStreamPosterFile -> autoStreamPosterPreviewUrl
useEffect(() => {
  if (!autoStreamPosterFile) {
    setAutoStreamPosterPreviewUrl("");
    return;
  }
  const u = URL.createObjectURL(autoStreamPosterFile);
  setAutoStreamPosterPreviewUrl(u);
  return () => URL.revokeObjectURL(u);
}, [autoStreamPosterFile]);

// autoStreamPosterLowFile -> autoStreamPosterLowPreviewUrl
useEffect(() => {
  if (!autoStreamPosterLowFile) {
    setAutoStreamPosterLowPreviewUrl("");
    return;
  }
  const u = URL.createObjectURL(autoStreamPosterLowFile);
  setAutoStreamPosterLowPreviewUrl(u);
  return () => URL.revokeObjectURL(u);
}, [autoStreamPosterLowFile]);


 

  // ====== MarketSub/Chip safe options ======
  const tree = TREE[category] || { chips: [], sub: [] };
  const chipOptions = tree.chips || [];
  const subOptions = tree.sub || [];
  const safeMarketSub = subOptions.includes(marketSub) ? marketSub : "";
  const safeMarketChip = chipOptions.includes(marketChip) ? marketChip : "";

  useEffect(() => {
    const chips = chipOptions;
    const subs = subOptions;

    setMarketChip((prev) => (chips.includes(prev) ? prev : chips[0] || ""));
    setMarketSub((prev) => (subs.includes(prev) ? prev : subs[0] || ""));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category]);

  // switch type -> hard reset opposite section
  useEffect(() => {
    setErr(null);
    setProgress({ video: 0, preview: 0, poster: 0, posterLow: 0, avatar: 0 });

    // очистка автопостеров
    setAutoStreamPosterFile(null);
    setAutoStreamPosterLowFile(null);

    if (type === "stream") {
      // stream: только видео
      setPosterFile(null);
      setPosterLowFile(null);
      if (!streamStatus) setStreamStatus("live");
    } else {
      // prediction: только постер
      setPreviewFile(null);
      setVideoFile(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type]);

  const flagsBooleans = useMemo(() => {
    const isNew = flags.includes("New");
    const isHot = flags.includes("Hot");
    const isBreaking = flags.includes("Breaking");
    return { isNew, isHot, isBreaking };
  }, [flags]);

  // stream: (опционально) генерим постер для превью карточки, как только выбран previewFile
  useEffect(() => {
    let cancelled = false;

    async function gen() {
      if (type !== "stream") return;
      if (!previewFile) {
        setAutoStreamPosterFile(null);
        setAutoStreamPosterLowFile(null);
        return;
      }
      try {
        const posterAuto = await capturePosterFromVideoFile(previewFile, 0.2);
        if (cancelled) return;
        if (!posterAuto) {
          setAutoStreamPosterFile(null);
          setAutoStreamPosterLowFile(null);
          return;
        }
        const low = await makeImageLow(posterAuto, 480, 0.72);
        if (cancelled) return;

        setAutoStreamPosterFile(posterAuto);
        setAutoStreamPosterLowFile(low || null);
      } catch {
        if (!cancelled) {
          setAutoStreamPosterFile(null);
          setAutoStreamPosterLowFile(null);
        }
      }
    }

    gen();
    return () => {
      cancelled = true;
    };
  }, [type, previewFile]);

  const canCreate = useMemo(() => {
    if (!title.trim() || !subTitle.trim()) return false;

    if (type === "stream") {
      return Boolean(previewFile && videoFile);
    }
    // prediction
    return Boolean(posterFile && posterLowFile);
  }, [title, subTitle, type, previewFile, videoFile, posterFile, posterLowFile]);

  const reset = () => {
    setErr(null);
    setSaving(false);

    setType("stream");
    setStreamStatus("live");
    setCategory("Crypto");
    setFlags(["New"]);

    setMarketChip(TREE.Crypto?.chips?.[0] || "");
    setMarketSub(TREE.Crypto?.sub?.[0] || "");

    setTitle("");
    setSubTitle("");

    setPreviewFile(null);
    setVideoFile(null);

    setPosterFile(null);
    setPosterLowFile(null);

    setAvatarFile(null);

    setAutoStreamPosterFile(null);
    setAutoStreamPosterLowFile(null);

    setProgress({ video: 0, preview: 0, poster: 0, posterLow: 0, avatar: 0 });
  };

  const create = async () => {
    if (!canCreate || saving) return;

    setSaving(true);
    setErr(null);
    setProgress({ video: 0, preview: 0, poster: 0, posterLow: 0, avatar: 0 });

    try {
      if (!db) throw new Error("Firestore db is missing (check firebase import)");
      if (!storage) throw new Error("Firebase storage is missing (check firebase import)");

      // мягкая проверка роли (не ломает publish)
      try {
        const u = auth?.currentUser;
        if (u?.getIdTokenResult) {
          const token = await u.getIdTokenResult(true);
          const role = token?.claims?.role;
          console.log("[AdminMarketPublish] role:", role);
        }
      } catch (e) {
        console.warn("[AdminMarketPublish] admin-check skipped:", e?.message || e);
      }

      const colPath = type === "stream" ? "avatar_streams" : "promos";
      const refDoc = doc(collection(db, colPath));
      const id = refDoc.id;

      const slug = slugifyTitle(title || subTitle || id);
      const basePath = type === "stream" ? "/streamevent" : "/event";
      const path = `${basePath}/${slug}-${id}`;

      const shareUrl = typeof window !== "undefined" ? `${window.location.origin}${path}` : path;

      const baseDraft = {
        id,
        type, // "stream" | "prediction"
        name: title.trim(),
        handle: subTitle.trim(),
        category: category, 
        market: category,
        flags: { ...flagsBooleans },

        marketSub: marketSub || "",
        marketChip: marketChip || "",

        avatarUrl: "",
        poster: "",
        posterLow: "",

        // stream only:
        videoSrc: "",
        previewSrc: "",

        // prediction: не используем imageSrc вообще (оставляем пустым для совместимости)
        imageSrc: "",
        imageLow: "",

        views: 0,

        shareUrl,
        slug,
        eventPath: path,

        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      const typedDraft =
        type === "stream"
          ? { status: streamStatus || "live", autoplay: true, loop: true, controls: true }
          : { status: "approved", isForecast: true, autoplay: false, loop: false, controls: false };

      // 1) create draft doc first
      await setDoc(refDoc, { ...baseDraft, ...typedDraft });

      // 2) upload according to type
      if (type === "stream") {
        if (!previewFile || !videoFile) throw new Error("Preview and main video are required for stream.");

        // poster + posterLow from preview
        const posterAuto = await capturePosterFromVideoFile(previewFile, 0.2);
        if (!posterAuto) throw new Error("Failed to generate poster from preview video.");

        const posterLowAuto = await makeImageLow(posterAuto, 480, 0.72);
        if (!posterLowAuto) throw new Error("Failed to generate posterLow.");

        const [previewSrc, videoSrc, poster, posterLow, avatarUrl] = await Promise.all([
          uploadFile({
            storage,
            docId: id,
            type,
            file: previewFile,
            kind: "preview",
            onProgress: (p) => setProgress((x) => ({ ...x, preview: p })),
          }),
          uploadFile({
            storage,
            docId: id,
            type,
            file: videoFile,
            kind: "video",
            onProgress: (p) => setProgress((x) => ({ ...x, video: p })),
          }),
          uploadFile({
            storage,
            docId: id,
            type,
            file: posterAuto,
            kind: "poster",
            onProgress: (p) => setProgress((x) => ({ ...x, poster: p })),
          }),
          uploadFile({
            storage,
            docId: id,
            type,
            file: posterLowAuto,
            kind: "posterLow",
            onProgress: (p) => setProgress((x) => ({ ...x, posterLow: p })),
          }),
          avatarFile
            ? uploadFile({
                storage,
                docId: id,
                type,
                file: avatarFile,
                kind: "avatar",
                onProgress: (p) => setProgress((x) => ({ ...x, avatar: p })),
              })
            : Promise.resolve(""),
        ]);

        await updateDoc(refDoc, {
          previewSrc: previewSrc || "",
          videoSrc: videoSrc || "",
          poster: poster || "",
          posterLow: posterLow || "",
          avatarUrl: avatarUrl || "",
          // подчистим “лишнее”
          imageSrc: "",
          imageLow: "",
          updatedAt: serverTimestamp(),
        });
      } else {
        // prediction
        if (!posterFile) throw new Error("Poster is required for prediction.");
        if (!posterLowFile) throw new Error("posterLow is required (generation failed).");

        const [poster, posterLow, avatarUrl] = await Promise.all([
          uploadFile({
            storage,
            docId: id,
            type,
            file: posterFile,
            kind: "poster",
            onProgress: (p) => setProgress((x) => ({ ...x, poster: p })),
          }),
          uploadFile({
            storage,
            docId: id,
            type,
            file: posterLowFile,
            kind: "posterLow",
            onProgress: (p) => setProgress((x) => ({ ...x, posterLow: p })),
          }),
          avatarFile
            ? uploadFile({
                storage,
                docId: id,
                type,
                file: avatarFile,
                kind: "avatar",
                onProgress: (p) => setProgress((x) => ({ ...x, avatar: p })),
              })
            : Promise.resolve(""),
        ]);

        await updateDoc(refDoc, {
          poster: poster || "",
          posterLow: posterLow || "",
          avatarUrl: avatarUrl || "",
          // prediction без видео
          previewSrc: "",
          videoSrc: "",
          // prediction без imageSrc
          imageSrc: "",
          imageLow: "",
          updatedAt: serverTimestamp(),
        });
      }

      onCreated?.(id, type);
      reset();
      alert("✅ Published");
    } catch (e) {
      setErr(e);
      alert(e?.message || "Publish failed");
    } finally {
      setSaving(false);
    }
  };

  /* ===========================
     LIBRARY: streams + predictions
     =========================== */
  const [streams, setStreams] = useState([]);
  const [predictions, setPredictions] = useState([]);
  const [loadingStreams, setLoadingStreams] = useState(true);
  const [loadingPredictions, setLoadingPredictions] = useState(true);
  const [deletingId, setDeletingId] = useState(null);

  // subscribe avatar_streams
  useEffect(() => {
    if (!db) return;
    const qStreams = query(collection(db, "avatar_streams"), orderBy("createdAt", "desc"), limit(50));

    const unsub = onSnapshot(
      qStreams,
      (snap) => {
        const list = snap.docs.map((d) => ({
          id: d.id,
          __col: "avatar_streams",
          ...d.data(),
        }));
        setStreams(list);
        setLoadingStreams(false);
      },
      (err) => {
        console.error("[AdminMarketPublish] avatar_streams snapshot error:", err);
        setStreams([]);
        setLoadingStreams(false);
      }
    );

    return () => unsub();
  }, []);

  // subscribe promos (approved predictions)
  useEffect(() => {
    if (!db) return;
    const qPromos = query(collection(db, "promos"), where("status", "==", "approved"), orderBy("createdAt", "desc"), limit(50));

    const unsub = onSnapshot(
      qPromos,
      (snap) => {
        const list = snap.docs
          .map((d) => ({
            id: d.id,
            __col: "promos",
            ...d.data(),
          }))
          .filter((x) => x.type === "prediction" || x.isForecast === true || String(x.kind || "").toLowerCase() === "forecast");

        setPredictions(list);
        setLoadingPredictions(false);
      },
      (err) => {
        console.error("[AdminMarketPublish] promos snapshot error:", err);
        setPredictions([]);
        setLoadingPredictions(false);
      }
    );

    return () => unsub();
  }, []);

  const deleteItem = async (item) => {
    if (!item?.id || !item?.__col) return;
    const label = item.type === "prediction" ? "prediction" : "stream";

    const ok = window.confirm(`Удалить ${label} карточку "${item.name || item.title || item.handle || item.id}"?`);
    if (!ok) return;

    setDeletingId(item.id);
    try {
      await deleteDoc(doc(db, item.__col, item.id));

      const urls = [
        item.videoSrc,
        item.previewSrc,
        item.poster,
        item.posterLow,
        item.avatarUrl,
      ].filter(Boolean);

      await Promise.all(urls.map((u) => safeDeleteFile(storage, u)));

      alert("✅ Deleted");
    } catch (e) {
      console.error("[AdminMarketPublish] deleteItem error:", e);
      alert(e?.message || "Delete failed");
    } finally {
      setDeletingId(null);
    }
  };

  // UI helpers
  const sectionTitleSx = { color: UI.text, fontWeight: 950, fontSize: 13, mb: 1 };
  const pillSx = { color: UI.text, border: UI.borderSoft, background: "rgba(2,6,23,.22)" };

  const btnSx = {
    borderRadius: 2,
    fontWeight: 950,
    color: UI.text,
    borderColor: "rgba(255,255,255,.18)",
    background: "rgba(2,6,23,.20)",
    "&:hover": { borderColor: "rgba(56,189,248,.45)", background: "rgba(56,189,248,.07)" },
  };

  return (
    <Grid container spacing={2} alignItems="flex-start">
      {/* LEFT: FORM + LIBRARY */}
      <Grid item xs={12} lg={7}>
        {/* FORM */}
        <Card elevation={0} sx={{ background: UI.glass, border: UI.border, borderRadius: 3, mb: 3 }}>
          <CardContent>
            <Typography sx={{ color: UI.text, fontWeight: 980, fontSize: 18 }}>
              Publish (Stream / Prediction)
            </Typography>

            {err ? (
              <Alert severity="warning" sx={{ mt: 1.5 }}>
                {String(err?.message || err)}
              </Alert>
            ) : null}

            {/* 1) TYPE */}
            <Divider sx={{ my: 2, borderColor: "rgba(255,255,255,.10)" }} />
            <Typography sx={sectionTitleSx}>1) Type</Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  select
                  fullWidth
                  label="Type"
                  value={type}
                  onChange={(e) => setType(e.target.value)}
                  sx={fieldSx}
                  SelectProps={{ MenuProps: MENU_PROPS }}
                >
                  <MenuItem value="stream">stream</MenuItem>
                  <MenuItem value="prediction">prediction</MenuItem>
                </TextField>
              </Grid>
            </Grid>

            {/* 2) CHIPS */}
            <Divider sx={{ my: 2, borderColor: "rgba(255,255,255,.10)" }} />
            <Typography sx={sectionTitleSx}>2) Chips</Typography>

            <Grid container spacing={2}>
              {type === "stream" ? (
                <Grid item xs={12} md={4}>
                  <TextField
                    select
                    fullWidth
                    label="Live / Record"
                    value={streamStatus}
                    onChange={(e) => setStreamStatus(e.target.value)}
                    sx={fieldSx}
                    SelectProps={{ MenuProps: MENU_PROPS }}
                  >
                    <MenuItem value="live">Live</MenuItem>
                    <MenuItem value="record">Record</MenuItem>
                  </TextField>
                </Grid>
              ) : null}

              <Grid item xs={12} md={4}>
                <TextField
                  select
                  fullWidth
                  label="Category"
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  sx={fieldSx}
                  SelectProps={{ MenuProps: MENU_PROPS }}
                >
                  {TOP_RIGHT.map((x) => (
                    <MenuItem key={x} value={x}>
                      {x}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  select
                  fullWidth
                  label="Flag"
                  value={flags}
                  onChange={(e) =>
                    setFlags(typeof e.target.value === "string" ? e.target.value.split(",") : e.target.value)
                  }
                  sx={fieldSx}
                  SelectProps={{
                    multiple: true,
                    MenuProps: MENU_PROPS,
                    renderValue: (selected) => (selected?.length ? selected.join(", ") : "—"),
                  }}
                >
                  {FLAGS.map((f) => (
                    <MenuItem key={f} value={f}>
                      <Checkbox checked={flags.indexOf(f) > -1} />
                      <ListItemText primary={f} />
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12}>
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                  <Chip label={`TYPE: ${type.toUpperCase()}`} sx={pillSx} />
                  {type === "stream" ? <Chip label={streamStatus.toUpperCase()} sx={pillSx} /> : null}
                  <Chip label={category.toUpperCase()} sx={pillSx} />
                  {flagsBooleans.isNew ? <Chip label="NEW" sx={pillSx} /> : null}
                  {flagsBooleans.isHot ? <Chip label="HOT" sx={pillSx} /> : null}
                  {flagsBooleans.isBreaking ? <Chip label="BREAKING" sx={pillSx} /> : null}
                </Box>
              </Grid>
            </Grid>

            {/* 3) MarketSub / MarketChip */}
            <Divider sx={{ my: 2, borderColor: "rgba(255,255,255,.10)" }} />
            <Typography sx={sectionTitleSx}>3) MarketSub / MarketChip</Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  select
                  fullWidth
                  label="Market sub"
                  value={safeMarketSub}
                  onChange={(e) => setMarketSub(e.target.value)}
                  sx={fieldSx}
                  disabled={subOptions.length === 0}
                  helperText={subOptions.length === 0 ? "No sub options for this category" : "Pick from list"}
                  SelectProps={{ MenuProps: MENU_PROPS }}
                >
                  {(subOptions.length ? subOptions : [""]).map((x) => (
                    <MenuItem key={x || "none"} value={x}>
                      {x || "—"}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  select
                  fullWidth
                  label="Market chip"
                  value={safeMarketChip}
                  onChange={(e) => setMarketChip(e.target.value)}
                  sx={fieldSx}
                  disabled={chipOptions.length === 0}
                  helperText={chipOptions.length === 0 ? "No chip options for this category" : "Pick from list"}
                  SelectProps={{ MenuProps: MENU_PROPS }}
                >
                  {(chipOptions.length ? chipOptions : [""]).map((x) => (
                    <MenuItem key={x || "none"} value={x}>
                      {x || "—"}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            </Grid>

            {/* 4) Identity */}
            <Divider sx={{ my: 2, borderColor: "rgba(255,255,255,.10)" }} />
            <Typography sx={sectionTitleSx}>4) Identity</Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="Title" value={title} onChange={(e) => setTitle(e.target.value)} sx={fieldSx} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="SubTitle" value={subTitle} onChange={(e) => setSubTitle(e.target.value)} sx={fieldSx} />
              </Grid>
            </Grid>

            {/* 5) Files */}
            <Divider sx={{ my: 2, borderColor: "rgba(255,255,255,.10)" }} />
            <Typography sx={sectionTitleSx}>5) Media (files)</Typography>

            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 1.2 }}>
              {type === "stream" ? (
                <>
                  <Chip
                    label={previewFile ? `Preview: ${previewFile.name}` : "Preview: required (mp4/webm)"}
                    sx={pillSx}
                  />
                  <Chip
                    label={videoFile ? `Video: ${videoFile.name}` : "Main video: required (mp4/webm)"}
                    sx={pillSx}
                  />
                  <Chip
                    label={autoStreamPosterLowFile || autoStreamPosterFile ? "Poster: auto-generated" : "Poster: auto (after preview)"}
                    sx={pillSx}
                  />
                </>
              ) : (
                <>
                  <Chip
                    label={posterFile ? `Poster: ${posterFile.name}` : "Poster: required (jpg/png/webp)"}
                    sx={pillSx}
                  />
                  <Chip
                    label={posterLowFile ? "posterLow: generated" : "posterLow: generating..."}
                    sx={pillSx}
                  />
                </>
              )}

              <Chip label={avatarFile ? `Avatar: ${avatarFile.name}` : "Avatar: optional"} sx={pillSx} />
            </Box>

            {/* TYPE-SPECIFIC INPUTS */}
            {type === "stream" ? (
              <Grid container spacing={1}>
                <Grid item xs={12} md={6}>
                  <Button component="label" fullWidth variant="outlined" startIcon={<CloudUploadIcon />} sx={btnSx}>
                    Pick preview (required)
                    <input
                      hidden
                      type="file"
                      accept="video/mp4,video/webm"
                      onChange={(e) => {
                        const f = e.target.files?.[0] || null;
                        if (!f) return;
                        if (!/^video\/(mp4|webm)$/i.test(f.type)) {
                          alert("Upload MP4/WebM only.");
                          return;
                        }
                        setPreviewFile(f);
                      }}
                    />
                  </Button>
                  {saving && progress.preview > 0 ? (
                    <LinearProgress variant="determinate" value={progress.preview} sx={{ mt: 1, borderRadius: 2 }} />
                  ) : null}
                </Grid>

                <Grid item xs={12} md={6}>
                  <Button component="label" fullWidth variant="outlined" startIcon={<CloudUploadIcon />} sx={btnSx}>
                    Pick main video (required)
                    <input
                      hidden
                      type="file"
                      accept="video/mp4,video/webm"
                      onChange={(e) => {
                        const f = e.target.files?.[0] || null;
                        if (!f) return;
                        if (!/^video\/(mp4|webm)$/i.test(f.type)) {
                          alert("Upload MP4/WebM only.");
                          return;
                        }
                        setVideoFile(f);
                      }}
                    />
                  </Button>
                  {saving && progress.video > 0 ? (
                    <LinearProgress variant="determinate" value={progress.video} sx={{ mt: 1, borderRadius: 2 }} />
                  ) : null}
                </Grid>

                <Grid item xs={12} md={6}>
                  <Button component="label" fullWidth variant="outlined" startIcon={<CloudUploadIcon />} sx={btnSx}>
                    Pick avatar (optional)
                    <input
                      hidden
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      onChange={(e) => {
                        const f = e.target.files?.[0] || null;
                        if (!f) return;
                        if (!/^image\/(jpeg|png|webp)$/i.test(f.type)) {
                          alert("Upload JPG/PNG/WebP only.");
                          return;
                        }
                        setAvatarFile(f);
                      }}
                    />
                  </Button>
                  {saving && progress.avatar > 0 ? (
                    <LinearProgress variant="determinate" value={progress.avatar} sx={{ mt: 1, borderRadius: 2 }} />
                  ) : null}
                </Grid>
              </Grid>
            ) : (
              <Grid container spacing={1}>
                <Grid item xs={12} md={6}>
                  <Button component="label" fullWidth variant="outlined" startIcon={<CloudUploadIcon />} sx={btnSx}>
                    Pick poster (required)
                    <input
                      hidden
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      onChange={async (e) => {
                        const f = e.target.files?.[0] || null;
                        if (!f) return;
                        if (!/^image\/(jpeg|png|webp)$/i.test(f.type)) {
                          alert("Upload JPG/PNG/WebP only.");
                          return;
                        }

                        setPosterFile(f);
                        setPosterLowFile(null);

                        try {
                          const low = await makeImageLow(f, 480, 0.72);
                          if (!low) {
                            alert("Failed to generate posterLow. Try another image.");
                            return;
                          }
                          setPosterLowFile(low);
                        } catch {
                          alert("Failed to generate posterLow. Try another image.");
                        }
                      }}
                    />
                  </Button>

                  {saving && progress.poster > 0 ? (
                    <LinearProgress variant="determinate" value={progress.poster} sx={{ mt: 1, borderRadius: 2 }} />
                  ) : null}
                  {saving && progress.posterLow > 0 ? (
                    <LinearProgress variant="determinate" value={progress.posterLow} sx={{ mt: 1, borderRadius: 2 }} />
                  ) : null}
                </Grid>

                <Grid item xs={12} md={6}>
                  <Button component="label" fullWidth variant="outlined" startIcon={<CloudUploadIcon />} sx={btnSx}>
                    Pick avatar (optional)
                    <input
                      hidden
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      onChange={(e) => {
                        const f = e.target.files?.[0] || null;
                        if (!f) return;
                        if (!/^image\/(jpeg|png|webp)$/i.test(f.type)) {
                          alert("Upload JPG/PNG/WebP only.");
                          return;
                        }
                        setAvatarFile(f);
                      }}
                    />
                  </Button>
                  {saving && progress.avatar > 0 ? (
                    <LinearProgress variant="determinate" value={progress.avatar} sx={{ mt: 1, borderRadius: 2 }} />
                  ) : null}
                </Grid>
              </Grid>
            )}

            <Box sx={{ display: "flex", gap: 1, mt: 2, flexWrap: "wrap" }}>
              <Button
                variant="contained"
                onClick={create}
                disabled={!canCreate || saving}
                startIcon={<CheckCircleOutlineIcon />}
                sx={{
                  borderRadius: 2,
                  fontWeight: 980,
                  background: "linear-gradient(90deg, rgba(56,189,248,.35), rgba(34,197,94,.24))",
                  border: "1px solid rgba(255,255,255,.14)",
                  color: UI.text,
                  "&:hover": { filter: "brightness(1.05)" },
                }}
              >
                {saving ? "Publishing..." : "Publish"}
              </Button>

              <Button
                variant="outlined"
                onClick={reset}
                disabled={saving}
                startIcon={<CancelOutlinedIcon />}
                sx={{
                  borderRadius: 2,
                  fontWeight: 980,
                  color: UI.text,
                  borderColor: "rgba(255,255,255,.18)",
                  background: "rgba(2,6,23,.18)",
                  "&:hover": { borderColor: "rgba(251,113,133,.55)", background: "rgba(251,113,133,.08)" },
                }}
              >
                Reset
              </Button>
            </Box>

            <Typography sx={{ mt: 1.2, color: "rgba(148,163,184,.9)", fontSize: 12 }}>
              Storage: <b>avatar_streams/&lt;id&gt;/*</b> для stream и <b>market_items/&lt;id&gt;/*</b> для prediction
            </Typography>
          </CardContent>
        </Card>

        {/* LIBRARY */}
        <Card elevation={0} sx={{ background: UI.glass, border: UI.border, borderRadius: 3 }}>
          <CardContent>
            <Typography sx={{ color: UI.text, fontWeight: 980, fontSize: 16, mb: 1 }}>
              Published library
            </Typography>

            {/* STREAMS */}
            <Typography sx={{ color: UI.subtext, fontWeight: 900, mt: 1 }}>
              Streams (avatar_streams)
            </Typography>
            {loadingStreams ? (
              <Typography sx={{ color: UI.subtext, fontSize: 13, mt: 0.5 }}>Loading streams...</Typography>
            ) : streams.length === 0 ? (
              <Typography sx={{ color: UI.subtext, fontSize: 13, mt: 0.5 }}>No streams yet.</Typography>
            ) : (
              <Grid container spacing={1} sx={{ mt: 1 }}>
                {streams.map((s) => (
                  <Grid item xs={12} md={6} key={s.id}>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 1,
                        p: 1.2,
                        borderRadius: 2,
                        border: UI.borderSoft,
                        background: "rgba(15,23,42,.65)",
                      }}
                    >
                      <UnifiedMarketCard
                        compact
                        item={{
                          ...s,
                          type: "stream",
                          status: s.status || "live",
                        }}
                        onOpen={() => {}}
                        onPrimary={() => {}}
                        onTips={() => {}}
                        onBoost={() => {}}
                      />
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<DeleteOutlineIcon />}
                        onClick={() => deleteItem(s)}
                        disabled={deletingId === s.id}
                        sx={{
                          alignSelf: "flex-end",
                          borderRadius: 2,
                          fontSize: 11,
                          py: 0.5,
                          px: 1.5,
                          color: "#fecaca",
                          borderColor: "rgba(248,113,113,.6)",
                          "&:hover": {
                            borderColor: "rgba(248,113,113,1)",
                            background: "rgba(248,113,113,.08)",
                          },
                        }}
                      >
                        {deletingId === s.id ? "Deleting..." : "Delete"}
                      </Button>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            )}

            {/* PREDICTIONS */}
            <Divider sx={{ my: 2, borderColor: "rgba(255,255,255,.12)" }} />
            <Typography sx={{ color: UI.subtext, fontWeight: 900 }}>
              Predictions (promos)
            </Typography>
            {loadingPredictions ? (
              <Typography sx={{ color: UI.subtext, fontSize: 13, mt: 0.5 }}>Loading predictions...</Typography>
            ) : predictions.length === 0 ? (
              <Typography sx={{ color: UI.subtext, fontSize: 13, mt: 0.5 }}>No predictions yet.</Typography>
            ) : (
              <Grid container spacing={1} sx={{ mt: 1 }}>
                {predictions.map((p) => (
                  <Grid item xs={12} md={6} key={p.id}>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 1,
                        p: 1.2,
                        borderRadius: 2,
                        border: UI.borderSoft,
                        background: "rgba(15,23,42,.65)",
                      }}
                    >
                      <UnifiedMarketCard
                        compact
                        item={{
                          ...p,
                          type: "prediction",
                          status: p.status || "approved",
                        }}
                        onOpen={() => {}}
                        onPrimary={() => {}}
                        onTips={() => {}}
                        onBoost={() => {}}
                      />
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<DeleteOutlineIcon />}
                        onClick={() => deleteItem(p)}
                        disabled={deletingId === p.id}
                        sx={{
                          alignSelf: "flex-end",
                          borderRadius: 2,
                          fontSize: 11,
                          py: 0.5,
                          px: 1.5,
                          color: "#fecaca",
                          borderColor: "rgba(248,113,113,.6)",
                          "&:hover": {
                            borderColor: "rgba(248,113,113,1)",
                            background: "rgba(248,113,113,.08)",
                          },
                        }}
                      >
                        {deletingId === p.id ? "Deleting..." : "Delete"}
                      </Button>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            )}
          </CardContent>
        </Card>
      </Grid>

      {/* RIGHT: PREVIEW */}
      <Grid item xs={12} lg={5}>
        <Box sx={{ position: { lg: "sticky" }, top: { lg: 16 } }}>
          <Card elevation={0} sx={{ background: UI.glass, border: UI.border, borderRadius: 3 }}>
            <CardContent>
              <Typography sx={{ color: UI.subtext, fontWeight: 900, mb: 1 }}>Preview</Typography>

              <Box sx={{ maxWidth: 420, mx: "auto" }}>
                <UnifiedMarketCard
                  compact
                  item={{
                    type,
                    status: type === "stream" ? streamStatus : "approved",
                    name: title || "Title",
                    handle: subTitle || "@handle",
                    category,
                    flags: flagsBooleans,

                    avatarUrl: avatarPreviewUrl || "",

                    // IMPORTANT:
                    // prediction: показываем posterLow сначала, потом poster
                    // stream: показываем autoPosterLow/autoPoster
                    poster:
                      type === "prediction"
                        ? (posterLowPreviewUrl || posterPreviewUrl || "")
                        : (autoStreamPosterLowPreviewUrl || autoStreamPosterPreviewUrl || ""),

                    // stream media
                    previewSrc: type === "stream" ? (previewPreviewUrl || "") : "",
                    videoSrc: type === "stream" ? (videoPreviewUrl || "") : "",

                    // запретим imageSrc, чтобы не было “левого” пути
                    imageSrc: "",

                    views: 0,
                    shareUrl: "",
                    autoplay: false,
                    loop: false,
                    controls: true,
                  }}
                  onOpen={() => {}}
                  onPrimary={() => {}}
                  onTips={() => {}}
                  onBoost={() => {}}
                />
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Grid>
    </Grid>
  );
}
